import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import {
  Video,
  CalendarClock,
  Sparkles,
  Workflow,
  Users,
  FolderOpen,
  Brain,
  CreditCard,
  Globe,
  Shield,
  Mic,
  ScreenShare,
  ListTodo,
  Kanban,
  Bell,
  Languages,
} from "lucide-react";

const GROUPS = [
  {
    title: "Run meetings that actually finish",
    icon: Video,
    items: [
      { t: "HD video meetings", d: "Encrypted rooms with server-issued tokens. No 40-minute cliffs, even on Free." },
      { t: "Camera & mic preview", d: "Lobby with device picker before you hit join." },
      { t: "Screen sharing", d: "Share screens, apps, or browser tabs without sign-in friction." },
      { t: "Host controls", d: "End meeting for all, manage attendees, lock the room, raise hands." },
      { t: "Cloud recording", d: "Encrypted recordings stored in your private bucket — yours forever." },
      { t: "Meeting timer + analytics", d: "Track real meeting length, attendance, no-shows, follow-up velocity." },
      { t: "Live chat, notes, tasks", d: "All side-by-side with the video — every word + every task tied to the meeting." },
    ],
  },
  {
    title: "AI that finishes the work",
    icon: Sparkles,
    items: [
      { t: "AI-powered recaps", d: "Concise summaries that read like a smart teammate's — generated the moment the meeting ends." },
      { t: "Action items extracted", d: "Pulled from notes, chat, and live tasks, assigned to the right person." },
      { t: "Decision log", d: "Searchable record of what was agreed, by whom, when." },
      { t: "Follow-up email drafts", d: "Polished recap email ready to send to every attendee in one click." },
      { t: "Full transcripts", d: "Searchable, speaker-tagged, timestamped — perfect for review or compliance." },
      { t: "One-click delivery", d: "Send the summary, action items, transcript, and recording to all attendees in a single click." },
    ],
  },
  {
    title: "Booking that feels custom-built",
    icon: CalendarClock,
    items: [
      { t: "Custom booking pages", d: "Per-appointment direct URLs (/a/<slug>), per-link menu pages (/b/<slug>) — pick the flow that fits." },
      { t: "Multiple appointment types", d: "Free intro calls, paid coaching, group sessions, recurring memberships — all on one workspace." },
      { t: "Rich intake forms", d: "Drag-to-reorder questions, conditional fields, structured addresses, file uploads, custom rich-text sections." },
      { t: "Multi-location appointments", d: "In-person, online, hybrid, phone — visitor picks at booking time. Map embeds for physical locations." },
      { t: "2-way Google Calendar sync", d: "Free/busy checks across multiple calendars, conflicts blocked before the booking confirms." },
      { t: "Deposits & invoicing (coming soon)", d: "Collect a deposit at booking and invoice the balance. Stripe-powered." },
      { t: "Approval workflow", d: "Optional manual approval per appointment type. Notification emails to admin + invitee." },
      { t: "Public/private toggle", d: "Internal-only appointments stay off your booking menus but stay bookable via direct link." },
    ],
  },
  {
    title: "Clients tracked, work moved",
    icon: Users,
    items: [
      { t: "Auto-contact CRM", d: "Every booking spawns a contact with a 7-digit customer ID, history, and notes." },
      { t: "Customer profiles", d: "Past bookings, total paid, notes from prior sessions, invoice history — one click away." },
      { t: "Multiple workspaces", d: "One for client work, one for the team, switch instantly." },
      { t: "Workspace roles", d: "Owner, admin, host, co-host, attendee, and guest invites — permissions done right." },
      { t: "Shared file library", d: "PDFs, briefs, recordings — all tied to the meeting or contact." },
      { t: "Internal kanban roadmap", d: "Plan your own team's work without leaving Bloq." },
      { t: "World clocks", d: "Three timezones at a glance, set per user. The remote-team essential." },
    ],
  },
];

const SECURITY = [
  { icon: Shield, t: "End-to-end token security", d: "Auth tokens never touch the client — every join is server-validated." },
  { icon: Languages, t: "Unguessable meeting IDs", d: "Random 12-char URL-safe slugs. No sequential IDs to enumerate." },
  { icon: Globe, t: "Workspace isolation", d: "Bloq can't even show you another workspace's data — enforced at the query layer, not the UI." },
];

export default function Features() {
  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container">
          <div className="max-w-2xl mb-16 bloq-fade-up">
            <p className="bloq-eyebrow">Features</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              Every part of the meeting, <span className="bloq-gradient-text">covered.</span>
            </h1>
            <p className="text-lg text-[#4a4a52] leading-relaxed">
              Bloq is what you'd build if you ran your business on conversations and were tired of duct tape.
            </p>
          </div>

          <div className="flex flex-col gap-16">
            {GROUPS.map((g, gi) => {
              const Icon = g.icon;
              return (
                <div key={g.title} className="grid lg:grid-cols-12 gap-10">
                  <div className="lg:col-span-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="bloq-headline text-3xl mb-3">{g.title}</p>
                  </div>
                  <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                    {g.items.map((it) => (
                      <div key={it.t} className="bloq-card p-6">
                        <p className="font-semibold mb-1.5 text-[#005e7a]">{it.t}</p>
                        <p className="text-sm text-[#4a4a52] leading-relaxed">{it.d}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-24 p-10 rounded-2xl bloq-glass-strong">
            <p className="bloq-eyebrow mb-3">Security by default</p>
            <h3 className="bloq-headline text-3xl mb-8 max-w-xl">
              Built with the boring-but-important stuff already done.
            </h3>
            <div className="grid sm:grid-cols-3 gap-5">
              {SECURITY.map((s) => {
                const I = s.icon;
                return (
                  <div key={s.t} className="flex items-start gap-3">
                    <I className="w-5 h-5 text-[#005e7a] mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">{s.t}</p>
                      <p className="text-sm text-[#4a4a52] leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
